import { useParams, useRouterState } from '@tanstack/react-router'
import type { BootstrapWorkspaceRef } from '@/features/bootstrap/domain/bootstrap-workspaces'
import { useBootstrapQuery } from '@/features/bootstrap/api/bootstrap/queries/bootstrap-query'
import {
  getBootstrapWorkspaceRefs,
  getGtmWorkspaceRefs,
  getPreferredBootstrapWorkspaceId,
} from '@/features/bootstrap/domain/bootstrap-workspaces'
import { getLastWorkspaceId } from '@/shared/lib/last-workspace'
import { GTM_PRODUCT_SCOPE } from '@/shared/lib/gtm-product-scope'

const GTM_ROUTE_PATTERN = /\/workspace\/[^/]+\/gtm(?:\/|$)/

export function isGtmWorkspaceRoute(pathname: string): boolean {
  return GTM_ROUTE_PATTERN.test(pathname)
}

/**
 * Seat/trial upsells belong to the per-seat chat plans. A workspace with a
 * digital employee is billed by usage, so the shell never shows them there,
 * including on Agents, Automations and Settings opened from that workspace.
 */
export function shouldShowSeatPlanPrompts(options: {
  pathname: string
  workspace: BootstrapWorkspaceRef | null
}): boolean {
  if (isGtmWorkspaceRoute(options.pathname)) return false
  return !options.workspace || options.workspace.employees.length === 0
}

/**
 * The workspace the shell is showing. Org-level routes (Agents, Automations)
 * carry no workspace id, so they keep the last opened workspace as context.
 */
export function useWorkspaceShellContext() {
  const routeParams = useParams({ strict: false })
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const bootstrapQuery = useBootstrapQuery()
  const allWorkspaces = getBootstrapWorkspaceRefs(
    bootstrapQuery.data?.workspaces,
  )
  // Pages without a workspace in the URL fall back to a GTM workspace only.
  const fallbackWorkspaces = GTM_PRODUCT_SCOPE.legacyWorkspaceUi
    ? allWorkspaces
    : getGtmWorkspaceRefs(bootstrapQuery.data?.workspaces)
  const routeWorkspaceId =
    'workspaceId' in routeParams && typeof routeParams.workspaceId === 'string'
      ? routeParams.workspaceId
      : ''
  const workspaceId =
    routeWorkspaceId ||
    getPreferredBootstrapWorkspaceId(
      fallbackWorkspaces,
      getLastWorkspaceId(),
    ) ||
    ''
  const workspace =
    allWorkspaces.find((item) => item.id === workspaceId) ?? null

  return {
    pathname,
    workspaceId,
    workspace,
    isGtmRoute: isGtmWorkspaceRoute(pathname),
    showSeatPlanPrompts: shouldShowSeatPlanPrompts({ pathname, workspace }),
  }
}
